import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { ArrowRight, CheckCircle } from "lucide-react";
import { Helmet } from "react-helmet-async";

const IMAGE = "https://customer-assets.emergentagent.com/job_cayenne-processor/artifacts/zb452am0_IMG-20260415-WA0046.jpg";

export default function WholeFruitPage() {
  return (
    <main data-testid="whole-fruit-page" className="pt-20">
      <Helmet>
        <title>Fresh Whole Cayenne Pineapples | Novapine | Mkonge Farm Hluhluwe</title>
        <meta name="description" content="Buy fresh whole Cayenne pineapples direct from Mkonge Farm, Hluhluwe. Novapine supplies whole fruit to retailers, distributors, fresh produce markets, and consumers across South Africa. Farm-fresh quality guaranteed." />
        <meta name="keywords" content="whole pineapple, fresh pineapple supplier South Africa, Cayenne pineapple, buy pineapples bulk, Mkonge Farm pineapples, pineapple distributor, fresh fruit supply, Hluhluwe pineapple, Novapine whole fruit, pineapple farm South Africa" />
        <link rel="canonical" href="https://www.novapine.co.za/products/whole-fruit" />
        <meta property="og:title" content="Fresh Whole Cayenne Pineapples | Novapine | Mkonge Farm" />
        <meta property="og:description" content="Fresh whole Cayenne pineapples direct from Mkonge Farm. For retailers, distributors, produce markets, and consumers." />
        <meta property="og:image" content={IMAGE} />
      </Helmet>

      <section className="py-20 bg-novapine-brown">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <p className="font-heading text-xs uppercase tracking-[0.2em] font-semibold text-novapine-gold mb-4">Product</p>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tighter text-white mb-4">Fresh Whole Cayenne Pineapples</h1>
          <p className="font-body text-base text-white/70 max-w-lg mx-auto">Sourced directly from Mkonge Farm, Hluhluwe, KwaZulu-Natal.</p>
        </div>
      </section>

      <section className="py-24 bg-[#F9F6F0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img src={IMAGE} alt="Fresh whole Cayenne pineapples from Mkonge Farm" className="rounded-xl w-full h-[450px] object-cover" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-novapine-brown mb-6">Farm-Fresh Whole Pineapples</h2>
              <p className="font-body text-base text-novapine-brown/70 leading-relaxed mb-6">
                In addition to our processed range, Novapine supplies fresh whole Cayenne pineapples sourced directly from Mkonge Farm in Hluhluwe. Our pineapples are grown in the subtropical climate of KwaZulu-Natal, producing fruit with exceptional flavour, sweetness, and juiciness.
              </p>
              <p className="font-body text-base text-novapine-brown/70 leading-relaxed mb-8">
                Available for commercial buyers, retailers, fresh produce markets, and everyday consumers. We currently supply all major fresh produce markets in South Africa, including Johannesburg, Durban, Pretoria, and Springs.
              </p>
              <h3 className="font-heading font-semibold text-sm uppercase tracking-[0.1em] text-novapine-gold mb-4">Who We Supply</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-8">
                {["Fresh produce markets", "Retail and farm stalls", "Distributors and wholesalers", "Food service and hospitality", "Export markets", "Everyday consumers"].map((app) => (
                  <div key={app} className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-novapine-gold shrink-0" />
                    <span className="font-body text-sm text-novapine-brown/70">{app}</span>
                  </div>
                ))}
              </div>
              <Link to="/contact">
                <Button className="btn-gold rounded-md px-8 py-5 font-heading text-sm tracking-wide font-semibold">
                  Enquire About Whole Fruit <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-novapine-brown mb-6">Why Choose Mkonge Farm Pineapples?</h2>
          <div className="space-y-4">
            <p className="font-body text-base text-novapine-brown/70 leading-relaxed">
              <strong className="text-novapine-brown">Grown in Hluhluwe since 1996.</strong> Mkonge Farm has nearly 30 years of experience cultivating Cayenne pineapples in the ideal subtropical conditions of KwaZulu-Natal.
            </p>
            <p className="font-body text-base text-novapine-brown/70 leading-relaxed">
              <strong className="text-novapine-brown">Direct from grower.</strong> No middlemen — you're buying straight from the farm. This means fresher fruit, better prices, and a reliable supply chain.
            </p>
            <p className="font-body text-base text-novapine-brown/70 leading-relaxed">
              <strong className="text-novapine-brown">The Cayenne advantage.</strong> Our Cayenne pineapples are known for their exceptional juice content, balanced flavour, and versatility — outstanding for eating fresh and for processing.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-novapine-brown">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white mb-6">Need Fresh Pineapples?</h2>
          <p className="font-body text-base text-white/70 mb-10">Contact Lloyd or James to discuss supply, volumes, and delivery.</p>
          <Link to="/contact"><Button className="btn-gold rounded-md px-10 py-6 font-heading text-sm tracking-wide font-semibold">Get In Touch <ArrowRight size={16} className="ml-2" /></Button></Link>
        </div>
      </section>
    </main>
  );
}
